import { RenderOptions, RenderResult, render } from '@testing-library/preact';
import userEvent from '@testing-library/user-event';
import { ComponentProps } from 'preact';
import { RootEnvironment, RootEnvironmentProvider } from '../../UNSAFE_Environment';
import translations from '../../resources/nls/en/bundle';

type RootEnvironmentProviderProps = ComponentProps<typeof RootEnvironmentProvider>;

const defaultEnvironment: Partial<RootEnvironment> = {
  translations: { '@oracle/oraclejet-preact': translations },
  mode: 'test'
};

export const renderInEnv = (
  children: RootEnvironmentProviderProps['children'],
  environment = defaultEnvironment,
  options?: Omit<RenderOptions, 'queries'>
) => {
  // We merge the provided environment with the default environment here. This way tests
  // can override some part of the environment without having to provide the other parts.
  // For example, if a test wants to provide `user.locale`, then it can do:
  //
  // renderInEnv(<SomeComponent />, { user: { locale: 'en-US' } });
  //
  // With this call, the test do not have to provide `translations` and `mode`. The provided
  // `user` object will be merged with the default environment. The final environment object
  // will have `translations`, `mode`, and `user` properties specified in it.
  const mergedEnvironment = { ...defaultEnvironment, ...environment };

  const { rerender: _rerender, ...result } = render(
    <RootEnvironmentProvider environment={mergedEnvironment}>{children}</RootEnvironmentProvider>,
    options
  );
  const rerender = (children: RootEnvironmentProviderProps['children']) =>
    _rerender(
      <RootEnvironmentProvider environment={mergedEnvironment}>{children}</RootEnvironmentProvider>
    );
  return { rerender, ...result } as RenderResult;
};

export const renderAndSetupInEnv = (...args: Parameters<typeof renderInEnv>) => ({
  user: userEvent.setup(),
  ...renderInEnv(...args)
});
